// Profile modal — unified Profile / Availability / Settings tabs.
// Opens via `root.profileEmail`. Settings + Availability tabs are only
// editable when viewing your own profile (and gated to admin for some
// fields). Profile tab works for any team member.

// Every calendar function this file needs arrives through `el.call`, never an
// import (FA001). A relative sibling import makes the bundler inline the
// function file into this component file, so frank publishes a body whose
// identifier resolves to nothing; the deep
// `@symbo-ls/workspace-calendar/functions/*` bare-package subpath is not the
// alternative either — it self-imports this package's own barrel and throws a
// circular-import TDZ crash (confirmed live via `bun test`, see
// QuickCreate.js's note). `functions/index.js` registers calendarUtils,
// availabilityShape and availabilityUtils on `context.functions`, and
// `functions/eagerSlice.js` registers all three EAGERLY — which this file
// needs, because `openProfileModal` fires on routes that never fold the
// calendar context.
// The admin predicate is reached through `el.call`, NEVER by a static import
// of `@symbo-ls/workspace-shell/functions/roleHelpers.js`. frank externalizes
// a cross-library import, so the emitted body carries a lookup that resolves
// nowhere on the published plane: measured live on
// system--workspace-ui.at.symbo.ls, this file's `_canEditModal` threw
// `TypeError: this.currentUserIsAdmin is not a function` on every load, and
// the stubbed names it published (`currentUser`, `currentUserIsAdmin` as "")
// also SHADOWED the shell's real ones in the merged globalScope — which is
// what made the shell's own `el.call('currentUserIsAdmin')` sites throw too
// (WS-SHELL-ROLEHELPERS-UNDECLARED-LIB-FLOODS-CONSOLE-1).
// `el.call` reads `context.functions`, where the declared workspace-shell
// library really does land, and it returns `undefined` for a missing key
// instead of throwing — so the affordance fails CLOSED, and the server still
// enforces the real authorization. Same idiom as this file's own
// `el.call('findPerson', …)`.
const _isViewingSelf = (root) => root?.profileEmail && root.profileEmail === root.currentUserEmail
const _canEditModal = (el, root) => _isViewingSelf(root) || !!el.call('currentUserIsAdmin', root)
const _formatTime = (el, tz, lang) => (tz ? el.call('formatTzTime', Date.now(), tz, lang) : '')
const _timezoneOptions = () => {
  const list =
    typeof Intl !== 'undefined' && Intl.supportedValuesOf
      ? Intl.supportedValuesOf('timeZone')
      : null
  if (list && list.length) return list
  return []
}

// Tenure from a start date → "New" / "5 mo" / "2y 3mo". Em-dash when unknown.
const _calendarTenure = (startDate) => {
  if (!startDate) return '—'
  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) return '—'
  const months = Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44))
  if (months < 1) return 'calendar.profile.tenureNew'
  if (months < 12) return `${months} mo`
  const years = Math.floor(months / 12)
  const rem = months % 12
  return rem ? `${years}y ${rem}mo` : `${years}y`
}

// City + Country, falling back to the legacy "City, CC" location string.
const _location = (p) => {
  const parts = [p?.city, p?.country].filter(Boolean)
  if (parts.length) return parts.join(', ')
  return p?.location || '—'
}

// Primary team name from the Mongo-sourced team index on root. id-first —
// a non-mgmt viewer's rows carry no email (server projection), so resolve via
// the stable userId before the email fallback (tickets/fable.md P0 regression).
const _teamName = (p, root) => {
  const byId = p?.userId != null ? root?.teamsByUserId?.[String(p.userId)] : null
  const rows =
    (Array.isArray(byId) && byId.length && byId) ||
    root?.teamsByEmail?.[String(p?.email || '').toLowerCase()] ||
    []
  return rows[0]?.name || '—'
}

// Org role — root.roles.byUser is authoritative (distinguishes owner/admin);
// legacy person.roles[0] is the fallback, then plain 'member'.
const _orgRole = (p, root) => {
  const byUser = root?.roles?.byUser || {}
  const role =
    (p?.userId && byUser[p.userId]) || (Array.isArray(p?.roles) && p.roles[0]) || 'member'
  return String(role)
}

// Resolve a profile-detail field to a display string, always falling back to
// an em-dash so empty members read as intentional, not broken. Keyed by a
// STRING `field` (not a function prop — see CalProfileDetailRow). `root` is
// threaded through for the fields that live on root state (team, role). The
// Profile tab mirrors the important info on the full /profile page.
const _profileFieldValue = (el, field, p, root, lang) => {
  switch (field) {
    case 'position':
      return p?.position || '—'
    case 'team':
      return _teamName(p, root)
    case 'role':
      return _orgRole(p, root)
    case 'location':
      return _location(p)
    case 'tenure':
      return _calendarTenure(p?.startDate || p?.start_date)
    case 'localTime': {
      const t = _formatTime(el, p?.timezone, lang)
      return t ? `${t} · ${p.timezone}` : '—'
    }
    case 'github':
      return p?.github || '—'
    // Legacy calendar-centric fields (still resolvable for any caller that
    // passes them; the Profile tab itself no longer lists these).
    case 'weeklyHours':
      return p?.weeklyHours != null ? String(p.weeklyHours) : '—'
    case 'workingDays':
      return p?.workingDays || '—'
    case 'workingHours':
      return p?.workingHours ? `${p.workingHours.start} – ${p.workingHours.end}` : '—'
    default:
      return '—'
  }
}

// Write against the VIEWED person (root.profileEmail), not the caller — the
// Availability tab is editable by an admin viewing someone else, and the old
// currentUserEmail target silently landed those edits on the admin's own row.
// Settings-tab writes are unaffected (that tab only renders on self, where
// profileEmail === currentUserEmail).
const _writeUser = (root, patch) => {
  const target = root.profileEmail || root.currentUserEmail
  const next = (root.people || []).map((p) => (p.email === target ? { ...p, ...patch } : p))
  root.update({ people: next })
}

// 7×24 availability grid helpers

const _calendarIsAvailableDefault = (el, user, hour) => {
  const wh = user?.workingHours
  if (!wh) return true
  const minute = hour * 60
  const start = el.call('hhmmToMin', wh.start)
  const end = el.call('hhmmToMin', wh.end)
  return start < end ? minute >= start && minute < end : minute >= start || minute < end
}

const _calendarIsAvailableAt = (el, user, day, hour) => {
  if (!user) return false
  const grid = user.availabilityGrid
  if (grid && grid[day] && typeof grid[day][hour] === 'boolean') {
    return grid[day][hour]
  }
  return _calendarIsAvailableDefault(el, user, hour)
}

const _toggleCell = (el, root, day, hour) => {
  const u = (root.people || []).find((p) => p.email === root.profileEmail) || null
  // Self OR admin — a member sets their OWN availability from their profile.
  if (!u || !_canEditModal(el, root)) return
  const grid = u.availabilityGrid
    ? u.availabilityGrid.map((row) => row.slice())
    : Array.from({ length: 7 }, () =>
        Array.from({ length: 24 }, (_, h) => _calendarIsAvailableDefault(el, u, h))
      )
  grid[day][hour] = !grid[day][hour]
  _writeUser(root, { availabilityGrid: grid })
  // Persist to the server-side AvailabilityRule (debounced, per-user).
  el.call('saveAvailabilityGrid', root, u, grid)
}

const _setTab = (root, tab) => root.update({ profileModalTab: tab })

// Body-only component — backdrop, ESC binding, dismiss-on-backdrop are
// owned by @symbo.ls/windows ModalLayer. Triggered via
// `el.call('openProfileModal', email)` and closed via
// `el.call('closeProfileModal')` — helpers in workspace/functions/.
// `root.profileEmail` retained as data carrier so the body's reactive
// reads + chat hash sync continue to work.
export const CalProfileModal = {
  width: '100%',
  maxWidth: 'I',
  // #2343 + overflow fix — flex column so the header, tabs, and footer stay
  // PINNED while ONLY the tab panes scroll. A whole-modal `overflowY:auto`
  // scrolled the footer buttons (and the header X) out of view once the tall
  // Availability grid exceeded the cap (verified 2026-07-17: the footer
  // scrolled away past 82dvh). ModalWindow is overflow:visible / maxHeight:null
  // in the windows layer, so this body must self-cap — but the scroll now lives
  // on the inner ScrollArea, not the whole modal.
  display: 'flex',
  flexFlow: 'column',
  maxHeight: '82dvh',
  overflow: 'hidden',
  gap: 'A',

  // Pull the viewed person's server-side AvailabilityRule into root.people so
  // the Availability tab + summary show persisted data, not just local state.
  onCreate: (el, s) => {
    const person = el.call('findPerson', s.root.profileEmail)
    if (person) el.call('hydratePersonAvailability', s.root, person)
  },

  ModalHeader: {
    flexShrink: '0',
    Avatar: {
      order: -1,
      avatarType: 'initials',
      boxSize: 'D D',
      src: (el, s) => el.call('avatarUrl', el.call('findPerson', s.root.profileEmail)?.name)
    },

    Hgroup: {
      gap: '0',
      H: {
        margin: '0',
        text: (el, s) =>
          el.call('findPerson', s.root.profileEmail)?.name ||
          el.call('polyglot', 'calendar.profile.heading')
      },
      P: {
        text: (el, s) => {
          const p = el.call('findPerson', s.root.profileEmail)
          if (!p) return ''
          const byId = p.userId != null ? s.root?.teamsByUserId?.[String(p.userId)] : null
          const email = String(p.email || s.root.profileEmail || '').toLowerCase()
          const rows =
            (Array.isArray(byId) && byId.length && byId) || s.root?.teamsByEmail?.[email] || []
          const team = rows[0]?.name
          return [p.position, team].filter(Boolean).join(' · ')
        }
      },
      Email: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        fontFamily: 'monospace',
        text: (el, s) => el.call('findPerson', s.root.profileEmail)?.email || ''
      }
    },
    Icons: {
      // Fix the X position + look: the brand ModalHeader.Icons uses negative
      // margins (`-Y2 -Z1`) to pull the button off toward the window corner,
      // where the window's rounded-corner clip ate into the glyph (verified
      // 2026-07-17 on this canvas). Reset the margin and pin the button to the
      // top of the header row so the X sits cleanly inside the padding. The
      // button is already a themed CircleButton (NavbarButton) with a hover
      // state — no restyle needed. Override the inherited Close.onClick to
      // dispatch the windows-layer close (the stateKey walker no-ops now this
      // component dropped its stateKey). Full-page open moved to the footer's
      // explicit "Open profile" button (the bare expand glyph was cryptic).
      margin: '0',
      alignSelf: 'start',
      Close: {
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('closeProfileModal')
        }
      }
    }
  },

  Tabs: {
    flexShrink: '0',
    flow: 'x',
    gap: 'V',
    borderBottomWidth: 'hairline',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',
    childExtends: 'CalProfileTabBtn',
    children: [
      { tab: 'profile', label: 'calendar.profile.tabProfile' },
      { tab: 'availability', label: 'calendar.profile.tabAvailability' },
      {
        tab: 'settings',
        label: 'calendar.profile.tabSettings',
        hide: (el, s) => !_isViewingSelf(s.root)
      }
    ]
  },

  // Scrollable middle — ONLY the tab panes scroll; header/tabs/footer stay
  // pinned (the overflow-cuts-buttons fix). `minHeight:0` lets this flex child
  // shrink below its content height so `overflowY:auto` actually engages.
  ScrollArea: {
    flow: 'y',
    gap: 'A',
    flex: '1 1 auto',
    minHeight: '0',
    overflowY: 'auto',

    // ── Profile tab ──
    ProfilePane: {
      hide: (el, s) => (s.root.profileModalTab || 'profile') !== 'profile',
      flow: 'y',
      gap: 'A',

      DetailGrid: {
        flow: 'y',
        gap: 'Z',
        padding: 'Z 0',

        childExtends: 'CalProfileDetailRow',
        // v3.14: a function prop (valueGetter) on a childExtends child is
        // consumed as a reactive factory, NOT stored — so el.parent.valueGetter
        // came back undefined and every Value rendered blank. Pass a STRING
        // `field` key (accessible like `label`) and resolve it in Value.text.
        children: [
          { label: 'calendar.profile.fieldPosition', field: 'position' },
          { label: 'calendar.profile.fieldTeam', field: 'team' },
          { label: 'calendar.profile.fieldRole', field: 'role' },
          { label: 'calendar.profile.fieldLocation', field: 'location' },
          { label: 'calendar.profile.fieldLocalTime', field: 'localTime' },
          { label: 'calendar.profile.fieldGithub', field: 'github' },
          { label: 'calendar.profile.fieldTenure', field: 'tenure' }
        ]
      }
    },

    // ── Availability tab ──
    AvailabilityPane: {
      hide: (el, s) => s.root.profileModalTab !== 'availability',
      flow: 'y',
      gap: 'Z',

      Header: {
        flow: 'x',
        align: 'center space-between',

        Title: {
          tag: 'span',
          text: '{{ calendar.profile.availabilityHeading | polyglot }}',
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'subtitle'
        },
        Hint: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) => {
            if (_isViewingSelf(s.root)) return el.call('polyglot', 'calendar.profile.hintToggle')
            return el.call(
              'polyglot',
              el.call('currentUserIsAdmin', s.root)
                ? 'calendar.profile.hintToggleAdmin'
                : 'calendar.profile.hintReadOnly'
            )
          }
        }
      },

      // Schedule-derived, tz-honest one-liner — the read-only view for anyone
      // opening another member's profile ("Available now · until 18:00 · …").
      Summary: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'paragraph',
        // `el.call` binds `this` to the element (Rule 8), which is what the
        // helper needs: it translates every word of its answer through the
        // bundles itself, and an unbound call answers raw keys.
        text: (el, s) =>
          el.call('availabilitySummaryText', el.call('findPerson', s.root.profileEmail))
      },

      CalAvailabilityGrid: {},

      // Every field is the shared field kit (AppField + AppInput/AppSelect,
      // workspace-shared components/AppField.js): the caption label above
      // the control, the brand `field` fill with the hairline edge. The
      // control carries behaviour only; the kit owns the look.
      AvailFields: {
        flow: 'y',
        gap: 'Z1',
        padding: 'Z 0',
        childExtends: 'AppField',

        LocationField: {
          label: (el) => el.call('polyglot', 'calendar.profile.locationLabel'),
          AppInput: {
            type: 'text',
            placeholder: (el) => el.call('polyglot', 'calendar.profile.locationPlaceholder'),
            readonly: (el, s) => (_canEditModal(el, s.root) ? null : true),
            value: (el, s) => el.call('findPerson', s.root.profileEmail)?.location || '',
            onInput: (e, el, s) => {
              if (!_canEditModal(el, s.root)) return
              _writeUser(s.root, { location: e.target.value })
            }
          }
        },

        TimezoneField: {
          label: (el) => el.call('polyglot', 'calendar.profile.timezoneLabel'),
          AppSelect: {
            disabled: (el, s) => (_canEditModal(el, s.root) ? null : true),
            value: (el, s) => el.call('findPerson', s.root.profileEmail)?.timezone || '',
            onChange: (e, el, s) => {
              if (!_canEditModal(el, s.root)) return
              _writeUser(s.root, { timezone: e.target.value })
            },
            childExtends: {
              tag: 'option',
              value: (el, s) => s.value,
              text: (el, s) => s.text
            },
            childrenAs: 'state',
            children: (el, s) => {
              const list = _timezoneOptions()
              return [
                { value: '', text: el.call('polyglot', 'calendar.profile.timezonePlaceholder') },
                ...list.map((tz) => ({ value: tz, text: tz }))
              ]
            }
          }
        },

        WeeklyHoursField: {
          label: (el) => el.call('polyglot', 'calendar.profile.weeklyHoursLabel'),
          AppInput: {
            type: 'text',
            readonly: true,
            value: (el, s) => {
              const u = el.call('findPerson', s.root.profileEmail)
              if (!u) return '0'
              let count = 0
              for (let day = 0; day < 7; day++) {
                for (let h = 0; h < 24; h++) {
                  if (_calendarIsAvailableAt(el, u, day, h)) count++
                }
              }
              return `${count} h / week`
            }
          }
        }
      }
    },

    // ── Settings tab (self only) ──
    SettingsPane: {
      hide: (el, s) => s.root.profileModalTab !== 'settings',
      flow: 'y',
      gap: 'Z1',
      childExtends: 'AppField',

      NameField: {
        label: (el) => el.call('polyglot', 'calendar.profile.displayNameLabel'),
        AppInput: {
          type: 'text',
          placeholder: (el) => el.call('polyglot', 'calendar.profile.displayNamePlaceholder'),
          value: (el, s) => el.call('findPerson', s.root.profileEmail)?.name || '',
          onInput: (e, el, s) => _writeUser(s.root, { name: e.target.value })
        }
      },

      PositionField: {
        label: (el) => el.call('polyglot', 'calendar.profile.positionLabel'),
        AppInput: {
          type: 'text',
          placeholder: (el) => el.call('polyglot', 'calendar.profile.positionPlaceholder'),
          value: (el, s) => el.call('findPerson', s.root.profileEmail)?.position || '',
          onInput: (e, el, s) => _writeUser(s.root, { position: e.target.value })
        }
      },

      // Read-only team display. Team membership is managed from the
      // profile editor in MemberProfile (with a real picker); the
      // calendar profile popover surfaces it but doesn't edit. A read-only
      // kit input, like WeeklyHoursField above — it was a `div` dressed as
      // a field; the same team name now fills the field's value.
      TeamField: {
        label: (el) => el.call('polyglot', 'calendar.profile.teamLabel'),
        AppInput: {
          type: 'text',
          readonly: true,
          value: (el, s) => {
            const p = el.call('findPerson', s.root.profileEmail)
            const byId = p?.userId != null ? s.root?.teamsByUserId?.[String(p.userId)] : null
            const email = String(p?.email || s.root.profileEmail || '').toLowerCase()
            const rows =
              (Array.isArray(byId) && byId.length && byId) || s.root?.teamsByEmail?.[email] || []
            return rows[0]?.name || '—'
          }
        }
      },

      GitHubField: {
        label: (el) => el.call('polyglot', 'calendar.profile.githubLabel'),
        AppInput: {
          type: 'text',
          placeholder: (el) => el.call('polyglot', 'calendar.profile.githubPlaceholder'),
          value: (el, s) => el.call('findPerson', s.root.profileEmail)?.github || '',
          onInput: (e, el, s) => _writeUser(s.root, { github: e.target.value })
        }
      }
    }
  },

  // ── Footer — Open profile + Message (others). Right-aligned to match the
  // @symbo.ls/windows footer convention (ConfirmTemplate). Pinned (flexShrink:0)
  // so the action buttons never scroll out of view when the panes overflow. ──
  ModalFooter: {
    flexShrink: '0',
    flow: 'x',
    gap: 'Z1',
    align: 'center end',

    OpenProfileBtn: {
      extends: 'Button',
      theme: 'ghost',
      text: '{{ calendar.profile.openProfile | polyglot }}',
      onClick: (e, el, s) => {
        e.stopPropagation()
        const email = s.root.profileEmail
        if (!email) return
        s.root.update({ profileEmail: null, profileModalTab: 'profile' })
        let rootEl = el
        while (rootEl && !rootEl.routes) rootEl = rootEl.parent
        const router = el.context?.utils?.router
        if (router && rootEl)
          router(el.call('profilePath', email), rootEl, {}, { pushState: true, scrollToTop: true })
      }
    },

    MessageBtn: {
      extends: 'Button',
      theme: 'primary',
      // Only when viewing someone else — you don't DM yourself. A reactive
      // `display` factory (not `hide`) because the default-config Button ships a
      // static `display: 'inline-flex'` that defeats `hide`'s display:none —
      // overriding display directly is what actually toggles it.
      display: (el, s) => (_isViewingSelf(s.root) ? 'none' : 'inline-flex'),
      text: '{{ calendar.profile.message | polyglot }}',
      onClick: (e, el, s) => {
        e.stopPropagation()
        const email = s.root.profileEmail
        if (!email) return
        // Mirror MemberProfile's Message action: stash the pending DM email
        // and route to /chat, which opens (or creates) the direct thread.
        s.root.update({
          profileEmail: null,
          profileModalTab: 'profile',
          _pendingDmEmail: email
        })
        el.call('closeProfileModal')
        el.router('/chat', el.getRoot())
      }
    }
  }
}

export const CalProfileTabBtn = {
  tag: 'button',
  type: 'button',
  padding: 'Z A',
  borderWidth: '0',
  borderBottomWidth: '2px',
  borderBottomStyle: 'solid',
  borderBottomColor: (el, s) =>
    (s.root.profileModalTab || 'profile') === el.tab ? 'blue' : 'transparent',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: (el, s) => ((s.root.profileModalTab || 'profile') === el.tab ? '700' : '500'),
  color: (el, s) => ((s.root.profileModalTab || 'profile') === el.tab ? 'title' : 'caption'),
  text: (el) => el.call('polyglot', el.label || ''),
  marginBottom: '-1px',
  ':hover': { color: 'title' },
  onClick: (e, el, s) => _setTab(s.root, el.tab)
}

// Detail row — label left (caption), value right (bold title), hairline
// separator between rows. Mirrors the /profile page's detail-list rhythm so
// the modal reads as a compact version of the same surface.
export const CalProfileDetailRow = {
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'Z 0',
  borderBottomWidth: 'hairline',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  ':last-child': { borderBottomWidth: '0' },

  Label: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'caption',
    whiteSpace: 'nowrap',
    text: (el) => el.call('polyglot', el.parent.label || '')
  },
  Value: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    align: 'center right',
    textAlign: 'right',
    // `_calendarTenure` can return a polyglot KEY ('… .tenureNew'); every other value
    // is real data, which translate() passes through unchanged.
    text: (el, s) => {
      const p = el.call('findPerson', s.root.profileEmail)
      return el.call(
        'polyglot',
        _profileFieldValue(el, el.parent.field, p, s.root, el.call('getActiveLang'))
      )
    }
  }
}

export const CalProfileUnavailRow = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Y Z',
  round: 'radiusControl',
  background: 'red.10',

  TypeBadge: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    textTransform: 'uppercase',
    color: 'red',
    text: (el, s) => s.type || ''
  },
  Title: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    flex: '1',
    text: (el, s) => s.title || el.call('polyglot', 'calendar.profile.noTitle')
  },
  Range: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontFamily: 'monospace',
    text: (el, s) => {
      if (s.endDate && s.endDate !== s.date) return `${s.date} → ${s.endDate}`
      return `${s.date} ${s.startTime || ''}–${s.endTime || ''}`
    }
  }
}

// ── Availability grid (used by AvailabilityPane) ──

export const CalAvailabilityGrid = {
  display: 'grid',
  // Bare `1fr` × 24 hour-columns floors each at its cell's own min-content
  // width, so the full 24-track row always summed wider than a narrow
  // container. `minmax(0, 1fr)` lets each hour column actually share the
  // available row width.
  gridTemplateColumns: 'auto repeat(24, minmax(0, 1fr))',
  gridTemplateRows: 'auto repeat(7, auto)',
  // `gap`/`padding` were a raw sub-token '1px' (design-system violation —
  // tokens only, no raw px). That wasn't just a token-grammar miss: verified
  // live (puppeteer DOM probe + screenshot) that each hour column renders at
  // ~15px wide holding a 2-char monospace label, so the true visual gap
  // between adjacent "00"/"01"/… cells collapsed to ~1px — legible as a
  // single unbroken run of digits, not 24 separated hour labels. `Y` is the
  // smallest step on the spacing scale — real breathing room between cells
  // without loosening the grid's compact, table-like density. `gridTemplateColumns`
  // itself is untouched (the `minmax(0, 1fr)` comment above is a prior,
  // deliberate overflow fix — don't reintroduce that bug by raising the floor).
  gap: 'Y',
  background: 'line',
  padding: 'Y',
  round: 'radiusControl',
  overflow: 'hidden',
  userSelect: 'none',

  Corner: {
    background: 'modal',
    padding: 'Y'
  },

  HourHeaders: {
    display: 'contents',
    childExtends: 'CalAvailabilityHourHeader',
    childrenAs: 'state',
    children: () => Array.from({ length: 24 }, (_, h) => ({ h }))
  },

  DayRows: {
    display: 'contents',
    // `DAYS` used to be a module-scope const read by this factory (frank
    // FA204 single-component-const — a value used by exactly one component
    // is better carried as `scope: {}` than a bare module-scope binding, no
    // globalScope indirection needed). See tickets/fable.md "workspace-
    // calendar frank serialization blockers".
    scope: { DAYS: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
    childExtends: 'CalAvailabilityDayRow',
    childrenAs: 'state',
    children: (el) => el.scope.DAYS.map((label, day) => ({ day, label }))
  }
}

export const CalAvailabilityHourHeader = {
  tag: 'span',
  background: 'modal',
  fontSize: 'Y2',
  color: 'caption',
  fontFamily: 'monospace',
  textAlign: 'center',
  padding: 'Y 0',
  text: (el, s) => String(s.h).padStart(2, '0')
}

export const CalAvailabilityDayRow = {
  display: 'contents',

  Label: {
    tag: 'span',
    background: 'modal',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    padding: 'Y Z',
    align: 'center right',
    text: (el, s) => s.label
  },

  Cells: {
    display: 'contents',
    childExtends: 'CalAvailabilityCell',
    childrenAs: 'state',
    children: (el, s) => Array.from({ length: 24 }, (_, h) => ({ day: s.day, h }))
  }
}

export const CalAvailabilityCell = {
  tag: 'button',
  border: 'none',
  padding: '0',
  height: 'A',
  minHeight: 'A',
  background: (el, s) => {
    const u = el.call('findPerson', el.state.root.profileEmail)
    return _calendarIsAvailableAt(el, u, s.day, s.h) ? 'green' : 'red.15'
  },
  cursor: (el, s) => (_canEditModal(el, el.state.root) ? 'pointer' : 'not-allowed'),
  // Cell fill IS the data (green/red-tint) — a neutral 'hover' wash would
  // mute the signal, and it's too subtle to read per-cell in a 168-cell
  // grid. Hued-surface exception (§4.3): brightness step, not opacity.
  ':hover': { filter: 'brightness(1.12)' },
  onClick: (e, el, s) => {
    e.preventDefault()
    _toggleCell(el, el.state.root, s.day, s.h)
  }
}
